/**
 * border-us slug — create-form input schema (shared transport layer).
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 */
import { z } from "zod";
import { OrderDocumentSourceTypeSchema } from "~/shared/documents/order-document-source";

import { optionalCustomsTextSchema } from "~/shared/documents/schemas/customs-input.schema";
import { DisplayUomOverrideSchema } from "~/shared/documents/schemas/document-display-uom.schema";
import { TypedSignatureInputSchema } from "~/shared/documents/schemas/typed-signature.schema";
import { DocumentShippableEntitySchema } from "~/utils/address-formatters";

const tscaCertificationSchema = z.enum(["comply", "not_subject"]).nullable().optional().default("comply");

/** US HTS — free text on create modal; schema accepts any string. */
const hsNumberSchema = z.string().trim().optional().default("");

export const BorderUsLineItemInputSchema = z.object({
  orderItemId: z.string().uuid(),
  description: z.string(),
  countryOfManufacture: optionalCustomsTextSchema,
  hsNumber: hsNumberSchema,
  tariffTreatment: optionalCustomsTextSchema,
  /** Prefilled from order line qty/UOM; broker may confirm or edit before create. PDF Qty/Packaging uses this value. */
  quantityDisplay: optionalCustomsTextSchema,
});

export const BorderUsDocumentInputSchema = z.object({
  /** Legacy transport name: identifies the source selected by sourceType. */
  orderId: z.string().uuid(),
  sourceType: OrderDocumentSourceTypeSchema.optional(),
  addressOverrides: z
    .object({
      operatingEntity: DocumentShippableEntitySchema.optional(),
      exporter: DocumentShippableEntitySchema.optional(),
      consignee: DocumentShippableEntitySchema.optional(),
      buyer: DocumentShippableEntitySchema.optional(),
      importerOfRecord: DocumentShippableEntitySchema.optional(),
    })
    .optional(),
  /** Prefilled filing entity block; broker may edit before create. PDF Importer of Record. */
  importerOfRecord: optionalCustomsTextSchema,
  /** Prefilled operating-entity contact block; broker may edit before create. PDF cover letter. */
  operatingEntity: optionalCustomsTextSchema,
  consignee: optionalCustomsTextSchema,
  buyer: optionalCustomsTextSchema,
  buyerFedId: optionalCustomsTextSchema,
  approximateCrossingDate: optionalCustomsTextSchema,
  /** Issuance-only pro forma date; never inferred from the crossing schedule. */
  invoiceDate: z.string().trim().optional(),
  /** Compatibility input; generation reloads Date of Sale from the source order date. */
  dateOfSale: z.string().trim().optional(),
  exportBorderCrossing: optionalCustomsTextSchema,
  importCrossingLocation: optionalCustomsTextSchema,
  /** Prefilled SO/PO reference; broker may edit. PDF Shipper/consignee ref#. */
  shipperConsigneeRef: optionalCustomsTextSchema,
  containerType: optionalCustomsTextSchema,
  exporter: optionalCustomsTextSchema,
  countryOfOrigin: optionalCustomsTextSchema,
  destinationCountry: optionalCustomsTextSchema,
  authorizedBy: optionalCustomsTextSchema,
  typedSignature: TypedSignatureInputSchema,
  lineItems: z.array(BorderUsLineItemInputSchema).min(1),
  tscaCertification: tscaCertificationSchema,
  displayUomOverride: DisplayUomOverrideSchema,
});

export type BorderUsDocumentInput = z.infer<typeof BorderUsDocumentInputSchema>;

/** Drafts may be unsigned; built-in issuance requires a deliberate positive TSCA signature. */
export const refineBorderUsDocumentIssuance = (input: BorderUsDocumentInput, ctx: z.RefinementCtx) => {
  if (!input.typedSignature.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["typedSignature"], message: "Enter a signature to certify TSCA compliance." });
  }
  if (input.tscaCertification === "not_subject") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["tscaCertification"],
      message: 'Built-in U.S. border documents require positive TSCA certification; "Not subject to TSCA" is not supported.',
    });
  }
};

export const BorderUsDocumentIssuanceInputSchema = BorderUsDocumentInputSchema.superRefine(refineBorderUsDocumentIssuance);
